/**
 * Country prefixes: voicepro.xenonlabs.ai/<prefix>.
 *
 * One table drives everything that depends on where a visitor is:
 *  - the CloudFront function sends a visitor from one of `countries` to `/<prefix>/` (its lookup tables are generated
 *    from this file: `pnpm exec tsx infra/cloudfront/sync-regions.ts`, checked by a test)
 *  - the server answers under each prefix as well as at the bare address (production only, see server/utils/base-path.ts)
 *  - the web app opens in `language` under that prefix unless the visitor picks another
 *
 * A prefix names a country, not a server: every prefix is served by the same deployment today, and one can move to
 * another region later by repointing its CloudFront behavior. To add a country, add a row. Countries not listed, India
 * included, stay on the bare address.
 */

export interface Region {
  /** Path segment without slashes: lowercase letters only, and not the first segment of any app route. */
  prefix: string;
  /** ISO 3166-1 alpha-2 codes of the visitors sent here (CloudFront's CloudFront-Viewer-Country header). */
  countries: string[];
  /** Default interface language (a code the app has translations for); unset = the browser's language. */
  language?: string;
}

/**
 * Only countries whose language Nova Sonic can speak (see NOVA_SONIC_LANGUAGES in server/services/nova-sonic-voices.ts:
 * Hindi, English US/GB/AU/IN, Spanish, French, German, Italian, Brazilian Portuguese). Not listed on purpose: Poland,
 * Sweden, Japan, Arabic-speaking countries (no Nova Sonic voice), Portugal (the model has only Brazilian Portuguese), and
 * the US and India (English is the default there, so a prefix adds nothing).
 */
export const REGIONS: readonly Region[] = [
  { prefix: 'de', countries: ['DE'], language: 'de' },
  { prefix: 'au', countries: ['AU', 'NZ'] },
  { prefix: 'uk', countries: ['GB'] },
  { prefix: 'fr', countries: ['FR'], language: 'fr' },
  { prefix: 'es', countries: ['ES'], language: 'es' },
  { prefix: 'it', countries: ['IT'], language: 'it' },
  { prefix: 'br', countries: ['BR'], language: 'pt' },
];

/** Every prefix, as '/de' style paths. */
export const REGION_PATHS: readonly string[] = REGIONS.map((r) => `/${r.prefix}`);

/** The default language for a prefix given as '/de' (or 'de'), if it has one. */
export function languageForRegionPath(path: string): string | undefined {
  const prefix = path.replace(/^\/+/, '');
  return REGIONS.find((r) => r.prefix === prefix)?.language;
}

/** The lookup tables and page filter the CloudFront function needs, as source text (see infra/cloudfront/sync-regions.ts). */
export function regionRouterTables(regions: readonly Region[] = REGIONS): string {
  const countryPrefix = regions.flatMap((r) => r.countries.map((c) => `${c}: '${r.prefix}'`)).join(', ');
  const prefixes = regions.map((r) => `${r.prefix}: 1`).join(', ');
  const notPages = ['api', 'assets', ...regions.map((r) => r.prefix), 'embed', 'uploads', 'images', 'audio-cache', 'ws', 'webhooks'].join('|');
  return [
    `var COUNTRY_PREFIX = { ${countryPrefix} };`,
    `var PREFIXES = { ${prefixes} };`,
    `var NOT_PAGES = /^\\/(${notPages})(\\/|$)/;`,
  ].join('\n');
}
